import { api } from '@/shared/lib/api';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { markCustomerAgentDisabled } from '../hooks/useCustomerAgentAvailability';
import { apiErrorCode, WHATSAPP_ERRORS } from '../lib/whatsapp-errors';
import type {
  CompleteWhatsappSignupRequest,
  WhatsappAbandonReason,
  WhatsappSignupSession,
  WhatsappStatus,
} from '../types/whatsapp.types';

const WHATSAPP = '/whatsapp';

/**
 * Toda chamada do canal passa por aqui: o 404 `whatsapp.disabled` marca a
 * loja como fora da área do Atendente IA, e a aba, a faixa e o assistente
 * somem na hora.
 */
async function gate<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (error) {
    if (apiErrorCode(error) === WHATSAPP_ERRORS.disabled) {
      markCustomerAgentDisabled(useAuthStore.getState().selectedTenant?.id ?? '');
    }
    throw error;
  }
}

async function data<T>(request: Promise<{ data: T }>): Promise<T> {
  const response = await gate(request);
  return response.data;
}

/** `GET /whatsapp/status` (`settings.read`). */
export const getWhatsappStatus = () => data(api.get<WhatsappStatus>(`${WHATSAPP}/status`));

/** `POST /whatsapp/connection/signup-sessions` (`whatsapp.manage`, direito do plano, aceite de Termos e DPA). */
export const createWhatsappSignupSession = () =>
  data(api.post<WhatsappSignupSession>(`${WHATSAPP}/connection/signup-sessions`));

/** `POST .../{sessionId}/complete`: devolve o status com a conexão nova. */
export const completeWhatsappSignupSession = (sessionId: string, request: CompleteWhatsappSignupRequest) =>
  data(
    api.post<WhatsappStatus>(
      `${WHATSAPP}/connection/signup-sessions/${encodeURIComponent(sessionId)}/complete`,
      request,
    ),
  );

/** `POST .../{sessionId}/abandon` (204). */
export const abandonWhatsappSignupSession = (sessionId: string, reason: WhatsappAbandonReason) =>
  gate(
    api.post(`${WHATSAPP}/connection/signup-sessions/${encodeURIComponent(sessionId)}/abandon`, { reason }),
  ).then(() => undefined);

/** `DELETE /whatsapp/connection` (204; 409 `whatsapp.not_connected`). */
export const disconnectWhatsapp = () => gate(api.delete(`${WHATSAPP}/connection`)).then(() => undefined);
